import React from "react";
import { Ico, StatusPill } from "./primitives.jsx";
import { useResource, apiPatch, navigate } from "./api.jsx";

const COLUMNS = [
  { key: "review",    title: "In review" },
  { key: "shortlist", title: "Shortlist" },
  { key: "diligence", title: "Diligence" },
  { key: "pass",      title: "Passed" },
];

export function Pipeline() {
  const list = useResource("/analyses/");

  const analyses = list.data?.results || [];
  const cols = COLUMNS.map(col => ({
    ...col,
    items: analyses.filter(a => (a.status || "review") === col.key),
  }));

  const goToResults = (id) => navigate("results", { analysisId: id });

  const moveTo = async (id, status) => {
    try {
      await apiPatch(`/analyses/${id}/status/`, { status });
      list.reload();
    } catch (err) {
      console.error("Failed to update status", err);
    }
  };

  return (
    <div className="page page--wide route-enter">
      <div className="page-header">
        <div>
          <div className="h-eyebrow" style={{marginBottom:8}}>Pipeline</div>
          <h1 className="page-title">Everything <em>inbound</em>.</h1>
        </div>
        <div className="row" style={{gap:10}}>
          <div className="seg"><button className="on">Board</button></div>
          <button className="btn btn--ghost" onClick={list.reload} title="Refresh the pipeline">Refresh</button>
          <button className="btn btn--primary" onClick={() => navigate("analyzer")}>
            <Ico n="upload" size={14}/> New
          </button>
        </div>
      </div>

      {list.loading && (
        <div className="card" style={{textAlign:"center", padding:40, color:"var(--text-mute)"}}>Loading pipeline…</div>
      )}
      {list.error && (
        <div className="card" style={{textAlign:"center", padding:40, color:"var(--bad)"}}>
          Couldn't load the pipeline. <button className="btn btn--ghost" style={{marginLeft:10}} onClick={list.reload}>Retry</button>
        </div>
      )}
      {!list.loading && !list.error && analyses.length === 0 && (
        <div className="card" style={{textAlign:"center", padding:60}}>
          <div className="h-eyebrow" style={{marginBottom:12}}>No inbound yet</div>
          <h2 className="h-display" style={{fontSize:"var(--text-2xl)", marginBottom:20}}>
            Your pipeline fills up as decks land.
          </h2>
          <button className="btn btn--primary" onClick={() => navigate("analyzer")}>
            <Ico n="upload" size={14}/> Upload a deck
          </button>
        </div>
      )}

      {!list.loading && !list.error && analyses.length > 0 && (
        <div style={{display:"grid", gridTemplateColumns:"repeat(4, 1fr)", gap:16}}>
          {cols.map(col=>(
            <div key={col.key} className="card card--flush">
              <div className="card-header">
                <div>
                  <div className="eyebrow">{col.title}</div>
                  <div className="title num">{col.items.length}</div>
                </div>
              </div>
              <div style={{padding:12, display:"flex", flexDirection:"column", gap:10}}>
                {col.items.length === 0 && (
                  <div style={{
                    padding:"20px 12px", textAlign:"center",
                    fontSize:12, color:"var(--text-dim)",
                    border:"1px dashed var(--line)", borderRadius:4,
                  }}>
                    Empty
                  </div>
                )}
                {col.items.map(c=>(
                  <div
                    key={c.id}
                    onClick={() => goToResults(c.id)}
                    style={{
                      padding:14, border:"1px solid var(--line)", borderRadius:4,
                      background:"var(--surface-2)", cursor:"pointer",
                    }}
                  >
                    <div style={{fontWeight:500, fontSize:14}}>{c.name}</div>
                    <div className="dim" style={{fontSize:12, marginTop:2}}>{c.tech || c.type || "—"}</div>
                    <div className="row" style={{marginTop:10}}>
                      <span className="num" style={{fontSize:16, fontWeight:500}}>
                        {c.ias != null ? Math.round(c.ias) : "—"}
                      </span>
                      <div className="spacer"/>
                      <span className="as-of">{c.stage || c.type || ""}</span>
                    </div>
                    <div style={{marginTop:10, display:"flex", gap:4, flexWrap:"wrap"}}>
                      {COLUMNS.filter(x => x.key !== col.key).map(x => (
                        <button
                          key={x.key}
                          onClick={(e) => { e.stopPropagation(); moveTo(c.id, x.key); }}
                          style={{
                            fontSize:10, letterSpacing:".06em", textTransform:"uppercase",
                            border:"1px solid var(--line)", borderRadius:3,
                            padding:"3px 6px", background:"transparent",
                            color:"var(--text-mute)", cursor:"pointer",
                          }}
                          title={`Move to ${x.title}`}
                        >
                          → {x.title}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
